import { createCaptureSource } from "@/hooks/voice/captureSource";
import { EphemeralDraftStorage } from "@/hooks/voice/voiceDraft";
import {
  createDefaultVoiceSocket,
  VoiceCancelledError,
  VoiceSessionController,
  type VoicePhase,
} from "@/hooks/voice/voiceSessionController";
import createDebug from "debug";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

const debug = createDebug("cohesive:live-transcription");

/** Cap for one dictation. Session chaining means this is a product choice
 * now, not the server's 30-minute ceiling. */
export const LIVE_MAX_RECORDING_DURATION = 1800;

/** startRecording() rejection meaning the user cancelled (or the hook
 * unmounted) during the connect phase. Callers must NOT treat this as
 * "engine unavailable" — falling back to another engine would start a
 * recording the user already dismissed. */
export class DictationCancelledError extends Error {
  constructor() {
    super("dictation cancelled");
    this.name = "DictationCancelledError";
  }
}

interface UseLiveTranscriptionOptions {
  /** Receives the final transcript (possibly empty) after a stop. */
  onTranscriptionComplete?: (text: string) => void;
  /** Receives user-ready error messages. */
  onError?: (error: Error) => void;
  onMaxDurationReached?: () => void;
}

interface UseLiveTranscriptionReturn {
  isRecording: boolean;
  isFinalizing: boolean;
  /** Full running transcript, updated per VAD segment while speaking. */
  interimText: string;
  audioLevel: number;
  error: string | null;
  duration: number;
  /** Rejects only during the connect phase (socket/`ready` failure) — the
   * caller may fall back to another engine. */
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  cancelRecording: () => void;
}

const RECORDING_PHASES: VoicePhase[] = [
  "live",
  "rotating",
  "reconnecting",
  "paused",
];

/**
 * Streaming dictation for the composer surfaces, implemented as a thin
 * adapter over {@link VoiceSessionController} — the same engine the mobile
 * voice screen runs on.
 *
 * Adopting it gives the web composer what it never had: a dropped connection
 * now reconnects and replays buffered audio instead of discarding the whole
 * transcript, and sessions chain past the server's 30-minute ceiling. The
 * public shape is unchanged, so `useDictation` and its three consumers are
 * untouched.
 *
 * Two deliberate differences from the mobile configuration:
 *  - `failFastOnFirstConnect`, so an unavailable endpoint (flag off at the
 *    backend, billing refusal) still degrades to the legacy HTTP engine
 *    rather than retrying against something that will not answer.
 *  - Ephemeral draft storage: the composer owns its own draft lifecycle and
 *    has no recovery UI, so persisting here would strand invisible drafts.
 */
export function useLiveTranscription(
  options: UseLiveTranscriptionOptions = {},
): UseLiveTranscriptionReturn {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const controllerRef = useRef<VoiceSessionController | null>(null);
  if (!controllerRef.current || controllerRef.current.isDisposed()) {
    controllerRef.current = new VoiceSessionController({
      createSocket: createDefaultVoiceSocket,
      createCapture: createCaptureSource,
      storage: new EphemeralDraftStorage(),
      config: {
        failFastOnFirstConnect: true,
        maxTotalRecordingSec: LIVE_MAX_RECORDING_DURATION,
      },
    });
  }
  const controller = controllerRef.current;
  controller.onMaxDurationReached = () =>
    optionsRef.current.onMaxDurationReached?.();

  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
  );

  const [audioLevel, setAudioLevel] = useState(0);

  // Deliver terminal outcomes as the callbacks the composer already expects,
  // then hand the controller back to idle so the next dictation can start.
  const prevPhaseRef = useRef<VoicePhase>(snapshot.phase);
  useEffect(() => {
    const previous = prevPhaseRef.current;
    prevPhaseRef.current = snapshot.phase;
    if (snapshot.phase === previous) return;

    if (snapshot.phase === "review") {
      const text = controller.getReviewText();
      debug("final transcript", { length: text.length });
      void controller.markDelivered();
      optionsRef.current.onTranscriptionComplete?.(text);
    } else if (snapshot.phase === "error") {
      const message = snapshot.error ?? "Transcription failed.";
      controller.acknowledgeError();
      optionsRef.current.onError?.(new Error(message));
    }
  }, [snapshot.phase, snapshot.error, controller]);

  // Levels are polled, never pushed through React per chunk; the composer
  // waveform wants a plain number, so mirror it at frame rate here.
  const isRecording = RECORDING_PHASES.includes(snapshot.phase);
  useEffect(() => {
    if (!isRecording) {
      setAudioLevel(0);
      return;
    }
    let frame = requestAnimationFrame(function tick() {
      setAudioLevel(controller.getAudioLevels().level);
      frame = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(frame);
  }, [isRecording, controller]);

  useEffect(() => {
    return () => {
      // The composer has no recovery surface, so an unmount mid-dictation
      // discards rather than stranding an invisible draft.
      void controller.cancel();
    };
  }, [controller]);

  const startRecording = useCallback(async () => {
    try {
      await controller.start({});
    } catch (startError) {
      if (startError instanceof VoiceCancelledError) {
        throw new DictationCancelledError();
      }
      debug("connect phase failed", startError);
      throw startError;
    }
  }, [controller]);

  const stopRecording = useCallback(() => {
    controller.stop();
  }, [controller]);

  const cancelRecording = useCallback(() => {
    void controller.cancel();
  }, [controller]);

  return {
    isRecording,
    isFinalizing: snapshot.phase === "finalizing",
    interimText: snapshot.fullText,
    audioLevel,
    error: snapshot.error,
    duration: snapshot.elapsedSec,
    startRecording,
    stopRecording,
    cancelRecording,
  };
}
